import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ExternalLink, MapPin, Search, ShieldCheck } from 'lucide-react';
import { Button, Card, Input } from '@mts/ui';
import { fetchListings } from '../services/api';
import { autocompleteClaimGooglePlaces, importClaimListingFromGooglePlace, suggestClaimGooglePlaceCategory } from '../services/claimFlow';
import { usePublicTenant } from '../hooks/usePublicTenant';
import PromotionBanner from '../components/promotion/PromotionBanner';

function normalizeClaimStatus(listing) {
  const ownershipStatus = String(listing?.ownershipStatus || listing?.claim?.status || '').trim().toLowerCase();
  if (ownershipStatus) return ownershipStatus;
  if (listing?.isClaimed === true || listing?.claimed === true) return 'claimed';
  return 'unclaimed';
}

function formatListingAddress(listing) {
  const parts = [listing?.addressLine1 || listing?.address, listing?.city, listing?.state, listing?.postalCode]
    .map((part) => String(part || '').trim())
    .filter(Boolean);

  return parts.join(', ');
}

function matchesListingSearch(listing, query) {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return true;

  const haystack = [
    listing?.name,
    listing?.category,
    listing?.primaryCategory,
    listing?.address,
    listing?.addressLine1,
    listing?.city,
    listing?.state,
    listing?.postalCode,
    listing?.phone,
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');

  return haystack.includes(normalizedQuery);
}

function getListingClaimPath(listing) {
  const publishedUrl = String(
    listing?.publicUrl
      || listing?.publicPath
      || listing?.listingUrl
      || listing?.listingURL
      || '',
  ).trim();

  if (publishedUrl) {
    try {
      const publishedPath = publishedUrl.startsWith('/')
        ? publishedUrl
        : new URL(publishedUrl).pathname;
      if (publishedPath.startsWith('/listings/')) {
        return `${publishedPath.replace(/\/$/, '').replace(/\/claim$/, '')}/claim`;
      }
    } catch {
      // Fall through to the listing identifier for malformed legacy URLs.
    }
  }

  const listingIdentifier = listing?.slug || listing?.id;
  const categorySlug = listing?.listingCategories
    ?.map((row) => row?.tenantCategory?.slug)
    ?.find(Boolean);
  if (listingIdentifier && categorySlug) {
    return `/listings/${encodeURIComponent(String(categorySlug))}/${encodeURIComponent(String(listingIdentifier))}/claim`;
  }
  return listingIdentifier
    ? `/listings/${encodeURIComponent(String(listingIdentifier))}/claim`
    : '';
}

const TRUST_CHIPS = [
  'Free to claim',
  'Takes under 2 minutes',
  'No credit card required',
];

const CLAIM_TIMELINE_STEPS = [
  'Find your business',
  'Verify ownership',
  'Customize your profile',
  'Start reaching local customers',
];

function createGoogleSessionToken() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 18)}`;
}

function ClaimNewPage() {
  const toast = useFeedback();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, tenantLogoConfig } = usePublicTenant();

  const [query, setQuery] = useState('');
  const planQuery = useMemo(() => {
    const next = new URLSearchParams();
    for (const key of ['plan', 'billing', 'productId', 'priceId']) if (searchParams.get(key)) next.set(key, searchParams.get(key));
    return next.toString();
  }, [searchParams]);
  useEffect(() => {
    if (planQuery) sessionStorage.setItem('mtsSelectedListingPlan', planQuery);
  }, [planQuery]);
  const [unclaimedListings, setUnclaimedListings] = useState([]);
  const [listingCount, setListingCount] = useState(null);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [listingsError, setListingsError] = useState('');

  const [googlePredictions, setGooglePredictions] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [selectedResultKey, setSelectedResultKey] = useState(null);
  const [claimingResultKey, setClaimingResultKey] = useState('');
  const [googleConfirmation, setGoogleConfirmation] = useState(null);
  const [googlePrimaryCategoryId, setGooglePrimaryCategoryId] = useState('');

  const googleRequestRef = useRef(0);
  const googleSessionTokenRef = useRef(createGoogleSessionToken());

  const filteredUnclaimed = useMemo(() => {
    const matches = unclaimedListings.filter((listing) => matchesListingSearch(listing, query));
    return matches.slice(0, 8);
  }, [unclaimedListings, query]);

  const unifiedResults = useMemo(() => {
    const localResults = filteredUnclaimed.map((listing) => ({
      key: `local:${String(listing.id || listing.slug || listing.name)}`,
      source: 'local',
      name: listing.name || 'Unnamed listing',
      subtitle: formatListingAddress(listing) || 'Address unavailable',
      listing,
    }));

    const googleResults = googlePredictions.map((prediction) => ({
      key: `google:${String(prediction.placeId || prediction.description)}`,
      source: 'google',
      name: prediction.name,
      subtitle: prediction.secondaryText || prediction.description,
      prediction,
    }));

    // Prioritize local unclaimed listings before Google Places suggestions.
    return [...localResults, ...googleResults];
  }, [filteredUnclaimed, googlePredictions]);

  useEffect(() => {
    let cancelled = false;

    async function loadListings() {
      setListingsLoading(true);
      setListingsError('');

      try {
        const allListings = await fetchListings();
        if (cancelled) return;

        setListingCount(allListings.length);
        const onlyUnclaimed = allListings.filter((listing) => normalizeClaimStatus(listing) !== 'claimed');
        setUnclaimedListings(onlyUnclaimed);
      } catch (error) {
        if (!cancelled) {
          setListingsError(error?.message || 'Unable to load listings.');
        }
      } finally {
        if (!cancelled) {
          setListingsLoading(false);
        }
      }
    }

    loadListings();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const trimmed = query.trim();

    if (!trimmed || trimmed.length < 2) {
      setGooglePredictions([]);
      setGoogleLoading(false);
      return;
    }

    const requestId = googleRequestRef.current + 1;
    googleRequestRef.current = requestId;

    setGoogleLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const payload = await autocompleteClaimGooglePlaces(trimmed, googleSessionTokenRef.current);
        const predictions = Array.isArray(payload?.predictions) ? payload.predictions : [];
        if (googleRequestRef.current !== requestId) return;

        const normalized = predictions.map((prediction) => ({
          placeId: prediction.placeId,
          name: prediction.name || prediction.description,
          secondaryText: prediction.secondaryText || '',
          description: prediction.description,
        }));

        setGooglePredictions(normalized.slice(0, 8));
        setGoogleError('');
      } catch (error) {
        if (googleRequestRef.current !== requestId) return;
        setGooglePredictions([]);
        setGoogleError(error?.message || 'Unable to fetch Google suggestions.');
      } finally {
        if (googleRequestRef.current === requestId) {
          setGoogleLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  useEffect(() => {
    if (!selectedResultKey) return;
    if (unifiedResults.some((result) => result.key === selectedResultKey)) return;
    setSelectedResultKey(null);
  }, [selectedResultKey, unifiedResults]);

  const handleClaimListing = (listing) => {
    const claimPath = getListingClaimPath(listing);
    if (!claimPath) return;

    const promo = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode');
    const params = new URLSearchParams(planQuery);
    if (promo) params.set('promo', promo);
    navigate(`${claimPath}${params.size ? `?${params.toString()}` : ''}`);
  };

  const shouldShowAutocomplete = query.trim().length >= 2;
  const tenantCity = String(tenant?.city || tenantLogoConfig?.cityName || '').trim();
  const businessLabel = listingCount === 1 ? 'business' : 'businesses';

  const handleResultClick = (resultKey) => {
    if (googleConfirmation?.resultKey !== resultKey) setGoogleConfirmation(null);
    setSelectedResultKey((current) => (current === resultKey ? null : resultKey));
  };

  const prepareGoogleClaim = async (prediction, resultKey) => {
    const placeId = String(prediction?.placeId || '').trim();
    if (!placeId) {
      setGoogleError('Google place id is missing for this result.');
      return;
    }

    try {
      setClaimingResultKey(resultKey);
      setGoogleError('');

      const payload = await suggestClaimGooglePlaceCategory(placeId, googleSessionTokenRef.current);
      setGoogleConfirmation({ ...payload, prediction, resultKey });
      setGooglePrimaryCategoryId(payload.categorySuggestion?.primaryCategory?.id || '');
    } catch (error) {
      toast.error(error?.message || 'Unable to load this Google listing right now.', { id: 'action:ClaimNewPage:prepareGoogleClaim' });
    } finally {
      setClaimingResultKey('');
    }
  };

  const handleGoogleClaim = async () => {
    const placeId = googleConfirmation?.place?.googlePlaceId || googleConfirmation?.prediction?.placeId;
    if (!placeId) return;
    try {
      setClaimingResultKey(googleConfirmation.resultKey);
      setGoogleError('');
      const secondary = googleConfirmation.categorySuggestion?.secondaryCategory;
      const payload = await importClaimListingFromGooglePlace(placeId, googleSessionTokenRef.current, {
        primaryCategoryId: googlePrimaryCategoryId || null,
        secondaryCategoryId: secondary?.parentId === googlePrimaryCategoryId ? secondary.id : null,
      });
      googleSessionTokenRef.current = createGoogleSessionToken();
      const claimPath = getListingClaimPath(payload?.listing);

      if (!claimPath) {
        throw new Error('Listing import succeeded but its published URL was not returned.');
      }

      const promo = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode');
      const params = new URLSearchParams(planQuery);
      if (promo) params.set('promo', promo);
      navigate(`${claimPath}${params.size ? `?${params.toString()}` : ''}`);
    } catch (error) {
      toast.error(error?.message || 'Unable to import this Google listing right now.', { id: 'action:ClaimNewPage:handleGoogleClaim' });
    } finally {
      setClaimingResultKey('');
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_38%),radial-gradient(circle_at_left,rgba(16,185,129,0.1),transparent_35%),linear-gradient(180deg,rgba(255,255,255,1),rgba(248,250,252,1))]">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <PromotionBanner />
        <Card shadow="md" padding="lg" className="space-y-5 border border-border/70 bg-gradient-to-br from-surface via-surface to-primary/5">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <ShieldCheck size={14} />
              Claim Verification
            </div>
            <h1 className="text-2xl font-bold text-[var(--primary)] sm:text-3xl">Claim Your Free Business Profile</h1>
            <p className="max-w-2xl text-sm text-text-secondary sm:text-base">
              Search your business name to find an existing unclaimed listing, or use Google Places suggestions to confirm your business details.
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {TRUST_CHIPS.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
                >
                  <span aria-hidden="true">✓</span>
                  {chip}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by business name"
              className="h-12 rounded-xl border-border pl-11 text-base"
            />
          </div>

          <div className="space-y-3">
            {shouldShowAutocomplete ? (
              <div className="overflow-hidden rounded-2xl border border-border/80 bg-surface shadow-lg">
                <div className="flex items-center justify-between border-b border-border/70 bg-surface/80 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Search results</p>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    {unifiedResults.length}
                  </span>
                </div>

                {listingsLoading ? <p className="px-3 py-3 text-sm text-text-secondary">Loading listings...</p> : null}
                {!listingsLoading && listingsError ? <InlineFeedback variant="error" className="px-3 py-3 text-sm text-error">{listingsError}</InlineFeedback> : null}
                {!googleLoading && googleError ? <InlineFeedback variant="error" className="px-3 py-3 text-sm text-warning">{googleError}</InlineFeedback> : null}
                {googleLoading ? <p className="px-3 py-2 text-xs font-semibold text-text-secondary">Searching Google Places...</p> : null}

                {!listingsLoading && !listingsError ? (
                  <div className="max-h-[26rem] overflow-y-auto">
                    {unifiedResults.map((result, index) => {
                      const isSelected = selectedResultKey === result.key;
                      const businessName = result.name || 'This business';

                      return (
                        <div key={result.key} className={index > 0 ? 'border-t border-border/60' : ''}>
                          <button
                            type="button"
                            onClick={() => handleResultClick(result.key)}
                            className={`w-full px-3 py-3 text-left transition hover:bg-primary/5 ${isSelected ? 'bg-primary/5' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <MapPin size={16} className="mt-0.5 shrink-0 text-text-secondary" />
                              <div className="min-w-0 flex-1 space-y-0.5">
                                <p className="truncate text-sm font-semibold text-text-primary">{businessName}</p>
                                <p className="truncate text-xs text-text-secondary">{result.subtitle}</p>
                              </div>
                              <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
                                {result.source === 'local' ? 'MTS' : 'Google'}
                              </span>
                            </div>
                          </button>

                          {isSelected ? (
                            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                              <Button
                                variant="primary"
                                className="h-9"
                                loading={claimingResultKey === result.key}
                                disabled={claimingResultKey === result.key}
                                onClick={() => {
                                  if (result.source === 'local') {
                                    handleClaimListing(result.listing);
                                    return;
                                  }
                                  prepareGoogleClaim(result.prediction, result.key);
                                }}
                              >
                                Claim {businessName}
                              </Button>

                              {result.source === 'google' ? (
                                <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
                                  Imports listing, then opens claim flow
                                  <ExternalLink size={13} />
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
                                  <Building2 size={13} />
                                  Continues to claim flow
                                </span>
                              )}
                              {result.source === 'google' && googleConfirmation?.resultKey === result.key ? (
                                <div className="mt-2 w-full rounded-xl border border-primary/20 bg-primary/5 p-3">
                                  <p className="text-sm font-semibold text-text-primary">{googleConfirmation.place?.name || businessName}</p>
                                  <p className="mt-1 text-xs text-text-secondary">{formatListingAddress(googleConfirmation.place)}</p>
                                  <label className="mt-3 block text-xs font-semibold text-text-primary">Primary Category
                                    <select value={googlePrimaryCategoryId} onChange={(event) => setGooglePrimaryCategoryId(event.target.value)} className="mt-1 h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm">
                                      <option value="">Unclassified</option>
                                      {(googleConfirmation.categories || []).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                                    </select>
                                  </label>
                                  {googleConfirmation.categorySuggestion?.secondaryCategory?.parentId === googlePrimaryCategoryId ? <p className="mt-2 text-xs text-text-secondary">Secondary Category: {googleConfirmation.categorySuggestion.secondaryCategory.name}</p> : null}
                                  <Button className="mt-3 h-9" loading={claimingResultKey === result.key} onClick={handleGoogleClaim}>Import and continue</Button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}

                    {!googleLoading && unifiedResults.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-text-secondary">No results found for that business name.</p>
                    ) : null}

                    {!googleLoading && unifiedResults.length <= 4 ? (
                      <button type="button" onClick={() => navigate(`/claim/new/manual?name=${encodeURIComponent(query.trim())}${searchParams.get('promo') ? `&promo=${encodeURIComponent(searchParams.get('promo'))}` : ''}`)} className="w-full border-t border-border/60 px-3 py-3 text-left text-sm font-semibold text-primary hover:bg-primary/5">Not Listed In Google? Add Manually</button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-border/70 bg-white/80 px-4 py-4 backdrop-blur-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">What Happens Next?</h2>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CLAIM_TIMELINE_STEPS.map((step, index) => (
                <div key={step} className="flex items-center gap-2 rounded-lg border border-border/60 bg-surface px-3 py-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <span className="text-sm text-text-primary">{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border/70 bg-primary/5 px-4 py-3">
            <p className="text-sm font-medium text-text-primary">
              {listingCount === null
                ? `Supporting ${tenantCity || 'local'} businesses`
                : `Trusted by ${listingCount.toLocaleString()} ${tenantCity || 'local'} ${businessLabel}`}
            </p>
          </div>

          <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-border/70 bg-surface px-4 py-3 sm:flex-row sm:items-center">
            <p className="inline-flex items-center gap-2 text-sm text-text-secondary">
              <MapPin size={14} />
              Can&apos;t find your listing yet? Use a Google suggestion, then contact support to add your business.
            </p>
            <Button variant="outline" onClick={() => navigate('/listings')}>Browse directory</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ClaimNewPage;
